// Required for Vercel Speed Insights and Analytics
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';

// Lazy load Sentry only in production to reduce initial bundle size
const initSentry = async () => {
  if (!import.meta.env.PROD) return;

  const Sentry = await import('@sentry/react');

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,

    // Environment detection
    environment:
      import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    enabled: true,

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance traces sample rate (10% of transactions)
    tracesSampleRate: 0.1,

    // Session replay sample rate
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Ignore common non-critical errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  });

  return Sentry;
};

// Initialize Sentry after first user interaction in production
if (import.meta.env.PROD) {
  let sentryInitialized = false;

  const startSentry = () => {
    if (sentryInitialized) return;
    sentryInitialized = true;
    initSentry();
    // Remove listeners after first initialization
    window.removeEventListener('click', startSentry);
    window.removeEventListener('scroll', startSentry);
    window.removeEventListener('keydown', startSentry);
  };

  window.addEventListener('click', startSentry, { once: true });
  window.addEventListener('scroll', startSentry, { once: true });
  window.addEventListener('keydown', startSentry, { once: true });

  // Fallback: initialize after 3 seconds if no interaction
  setTimeout(startSentry, 3000);
}

import { StrictMode, lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import {
  RouterProvider,
  createBrowserRouter,
  redirect,
} from 'react-router-dom';

import App from './App.tsx';
import {
  rootLoader,
  protectedLoader,
  loginLoader,
  // onboardingLoader, // Removed - no longer needed
  resetPasswordLoader,
} from './lib/loaders.ts';
import { ProtectedLayout } from './routes/protected-layout.tsx';
import { RootErrorBoundary } from './routes/root-error-boundary.tsx';

// Lazy load all page components for code splitting
const DashboardPage = lazy(() =>
  import('./pages/app/DashboardPage.tsx').then(m => ({
    default: m.DashboardPage,
  }))
);
const InsightsPage = lazy(() =>
  import('./pages/app/InsightsPage.tsx').then(m => ({
    default: m.InsightsPage,
  }))
);
const MusicPage = lazy(() =>
  import('./pages/app/MusicPage.tsx').then(m => ({ default: m.MusicPage }))
);
const ProfilePage = lazy(() => import('./pages/app/ProfilePage.tsx'));
const LandingPage = lazy(() =>
  import('./pages/LandingPage.tsx').then(m => ({ default: m.LandingPage }))
);
const LoginPage = lazy(() =>
  import('./pages/LoginPage.tsx').then(m => ({ default: m.LoginPage }))
);
const SignupPage = lazy(() =>
  import('./pages/SignupPage.tsx').then(m => ({ default: m.SignupPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage.tsx').then(m => ({
    default: m.ForgotPasswordPage,
  }))
);
const AuthCallbackPage = lazy(
  () => import('./pages/auth/AuthCallbackPage.tsx')
);
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage.tsx'));
const ResetPasswordPage = lazy(
  () => import('./pages/auth/ResetPasswordPage.tsx')
);
const PrivacyPage = lazy(() =>
  import('./pages/public/PrivacyPage.tsx').then(m => ({
    default: m.PrivacyPage,
  }))
);
const TermsPage = lazy(() =>
  import('./pages/public/TermsPage.tsx').then(m => ({ default: m.TermsPage }))
);
const RefundPage = lazy(() =>
  import('./pages/public/RefundPage.tsx').then(m => ({ default: m.RefundPage }))
);
const ContactPage = lazy(() =>
  import('./pages/public/ContactPage.tsx').then(m => ({
    default: m.ContactPage,
  }))
);
const PressPage = lazy(() =>
  import('./pages/public/PressPage.tsx').then(m => ({ default: m.PressPage }))
);
const PartnershipsPage = lazy(() =>
  import('./pages/public/PartnershipsPage.tsx').then(m => ({
    default: m.PartnershipsPage,
  }))
);
// Test pages temporarily disabled due to outdated API references
// const TasksTestPage = lazy(() => import('./pages/test/TasksTestPage.tsx'));
// const AudioTestPage = lazy(() =>
//   import('./pages/test/AudioTestPage.tsx').then((m) => ({
//     default: m.AudioTestPage,
//   }))
// );
const SuccessPage = lazy(() => import('./pages/upgrade/SuccessPage.tsx'));
const CancelPage = lazy(() => import('./pages/upgrade/CancelPage.tsx'));
// Onboarding removed - users go straight to dashboard
// const OnboardingFlow = lazy(() =>
//   import('./pages/onboarding/OnboardingFlow.tsx').then(m => ({
//     default: m.OnboardingFlow,
//   }))
// );

// Loading fallback component
export function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-bg-primary">
      {/* Radial Glow Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(139,111,203,0.15)_0%,_transparent_50%)] mix-blend-overlay opacity-80 pointer-events-none" />

      <div className="text-center relative z-10">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-purple border-t-transparent mx-auto mb-4" />
        <p className="text-sm text-text-secondary font-sans tracking-wide">
          Loading...
        </p>
      </div>
    </div>
  );
}

import './index.css';

// Initialize Tawk.to styling
import { initializeTawkStyles } from './lib/tawk-styles';
initializeTawkStyles();

// Load dev utilities in development mode
if (import.meta.env.DEV) {
  import('./utils/devUtils.ts');
}

const router = createBrowserRouter([
  {
    id: 'root',
    path: '/:lang?',
    element: <App />,
    loader: rootLoader,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoader />}>
            <LandingPage />
          </Suspense>
        ),
      },
      {
        path: 'login',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LoginPage />
          </Suspense>
        ),
        loader: loginLoader,
      },
      {
        path: 'signup',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SignupPage />
          </Suspense>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: 'auth/callback',
        element: (
          <Suspense fallback={<PageLoader />}>
            <AuthCallbackPage />
          </Suspense>
        ),
      },
      {
        path: 'auth/verify-email',
        element: (
          <Suspense fallback={<PageLoader />}>
            <VerifyEmailPage />
          </Suspense>
        ),
      },
      {
        path: 'auth/reset-password',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ResetPasswordPage />
          </Suspense>
        ),
        loader: resetPasswordLoader,
      },
      {
        path: 'privacy',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        ),
      },
      {
        path: 'terms',
        element: (
          <Suspense fallback={<PageLoader />}>
            <TermsPage />
          </Suspense>
        ),
      },
      {
        path: 'refund',
        element: (
          <Suspense fallback={<PageLoader />}>
            <RefundPage />
          </Suspense>
        ),
      },
      {
        path: 'contact',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ContactPage />
          </Suspense>
        ),
      },
      {
        path: 'press',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PressPage />
          </Suspense>
        ),
      },
      {
        path: 'partnerships',
        element: (
          <Suspense fallback={<PageLoader />}>
            <PartnershipsPage />
          </Suspense>
        ),
      },
      // Onboarding removed - users go straight to dashboard
      // {
      //   path: 'onboarding',
      //   element: (
      //     <Suspense fallback={<PageLoader />}>
      //       <OnboardingFlow />
      //     </Suspense>
      //   ),
      //   loader: onboardingLoader,
      // },
      // Test routes temporarily disabled due to outdated API references
      // {
      //   path: 'test/tasks',
      //   element: (
      //     <Suspense fallback={<PageLoader />}>
      //       <TasksTestPage />
      //     </Suspense>
      //   ),
      //   loader: protectedLoader,
      // },
      // {
      //   path: 'test/audio',
      //   element: (
      //     <Suspense fallback={<PageLoader />}>
      //       <AudioTestPage />
      //     </Suspense>
      //   ),
      // },
      {
        path: 'upgrade/success',
        element: (
          <Suspense fallback={<PageLoader />}>
            <SuccessPage />
          </Suspense>
        ),
      },
      {
        path: 'upgrade/cancel',
        element: (
          <Suspense fallback={<PageLoader />}>
            <CancelPage />
          </Suspense>
        ),
      },
      {
        path: 'app',
        element: <ProtectedLayout />,
        loader: protectedLoader,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<PageLoader />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: 'insights',
            element: (
              <Suspense fallback={<PageLoader />}>
                <InsightsPage />
              </Suspense>
            ),
          },
          {
            path: 'music',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MusicPage />
              </Suspense>
            ),
          },
          {
            path: 'profile',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ProfilePage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: '*',
        loader: () => redirect('/'),
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
    <Analytics />
    <SpeedInsights />
  </StrictMode>
);
